import type { IconName } from '@/components/Icon/iconMap';
import type { KeyCombo, ShortcutPlatform } from '../types';
import { parseKeyCombo } from './keyCombo';

/**
 * キーコンボの 1 トークン分の表示単位。
 * icon を持てばアイコン描画、なければ label をテキスト描画する（ShortcutLabel が分岐）。
 * label はテキスト描画値であると同時に、アイコン時の aria 用アクセシブル名にもなる。
 */
export type KeyComboPart = {
  iconName?: IconName;
  label: string;
};
type ModifierKey = 'ctrl' | 'alt' | 'shift' | 'meta';

// 表示順は formatKeyLabel と揃える（mac: ⌃⌥⇧⌘ / win: Ctrl Alt Shift Win）
const MODIFIER_ORDER: ModifierKey[] = ['ctrl', 'alt', 'shift', 'meta'];

/** mac のモディファイア + Return は lucide アイコンで表現する（ユーザー確定マッピング）。 */
const MAC_MODIFIER_PARTS: Record<ModifierKey, KeyComboPart> = {
  ctrl: {
    iconName: 'ChevronUpIcon',
    label: 'Control'
  },
  alt: {
    iconName: 'OptionIcon',
    label: 'Option'
  },
  shift: {
    iconName: 'ArrowBigUpIcon',
    label: 'Shift'
  },
  meta: {
    iconName: 'CommandIcon',
    label: 'Command'
  }
};

/**
 * win もアイコン表示は lucide（Control=chevron-up / Shift=arrow-big-up）に揃える。
 * Alt は lucide に等価記号がないためオリジナルアイコン。Win キーは registry で未使用のためテキスト。
 * label はテキスト表示（variant="text"）・アイコン時の aria に使う win 慣例の呼称。
 */
const WIN_MODIFIER_PARTS: Record<ModifierKey, KeyComboPart> = {
  ctrl: {
    iconName: 'ChevronUpIcon',
    label: 'Ctrl'
  },
  alt: {
    iconName: 'OriginalAltKeyWindowsIcon',
    label: 'Alt'
  },
  shift: {
    iconName: 'ArrowBigUpIcon',
    label: 'Shift'
  },
  meta: {
    label: 'Win'
  }
};

// 終端キーの記号ラベル（モディファイア以外）。Enter は両 platform でアイコン化する
const SPECIAL_KEY_LABELS: Record<string, string> = {
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  backspace: '⌫',
  escape: 'Esc',
  space: 'Space',
  tab: 'Tab'
};
function terminalKeyPart(key: string): KeyComboPart {
  if (key === 'enter') {
    return {
      iconName: 'CornerDownLeftIcon',
      label: 'Return'
    };
  }
  const special = SPECIAL_KEY_LABELS[key];
  if (special) return {
    label: special
  };
  return {
    label: key.length === 1 ? key.toUpperCase() : key
  };
}

/**
 * KeyCombo を表示単位（アイコン or テキスト）の列へ分解する。
 * 記号文字列を返す formatKeyLabel に対し、こちらはアイコン描画用（ShortcutLabel が利用）。
 */
export function keyComboParts(combo: KeyCombo, platform: ShortcutPlatform): KeyComboPart[] {
  const parsed = parseKeyCombo(combo);
  const modifierParts = platform === 'mac' ? MAC_MODIFIER_PARTS : WIN_MODIFIER_PARTS;
  const parts: KeyComboPart[] = [];
  for (const modifier of MODIFIER_ORDER) {
    if (parsed[modifier]) parts.push(modifierParts[modifier]);
  }
  if (parsed.key) parts.push(terminalKeyPart(parsed.key));
  return parts;
}